import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  InputAdornment,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, Check as CheckCircleIcon, Home, Send } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchOpportunities, markChatsAsRead } from '../../redux/slices/opportunitySlice';
import { OpportunitiesTable } from '../../components/DataTable/examples';
import { Opportunity } from '../../types';
import BackButton from '../../components/BackButton/BackButton';
import styles from './OpportunitiesPage.module.css';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import apiClient from '../../services/api-client';
import { useSnackbar } from 'notistack';

const OpportunitiesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();

  const { items: opportunities, loading, error } = useAppSelector(state => state.opportunities);

  // Estado para modal de detalhes
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Estados para chat
  const [activeTab, setActiveTab] = useState(0);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchOpportunities());
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Oportunidades' },
    ]);
  }, [dispatch, setBreadcrumbs]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleViewOpportunity = (opportunityId: string) => {
    navigate(`/opportunities/${opportunityId}`);
  };

  const handleEditOpportunity = (opportunityId: string) => {
    navigate(`/opportunities/edit/${opportunityId}`);
  };

  // Handler para abrir modal ao clicar na linha
  const handleTableRowClick = (row: { id: string }) => {
    const original = opportunities.find(o => o.id === row.id);
    if (original) {
      // Se a oportunidade tem chats não lidos, marcar como lido
      if (original.unread_chats) {
        dispatch(markChatsAsRead(original.id));
      }
      setSelectedOpportunity(original);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOpportunity(null);
  };

  // Transform opportunities data for the table
  const tableData = opportunities.map(opportunity => ({
    id: opportunity.id,
    client:
      opportunity.customer?.company_name ||
      opportunity.client_company_name ||
      'Cliente não identificado',
    title: opportunity.title,
    date: new Date(
      opportunity.estimated_closing_date || opportunity.expected_close_date || ''
    ).toLocaleDateString('pt-BR'),
    status:
      opportunity.status === 'Open'
        ? 'Em negociação'
        : opportunity.status === 'Won'
          ? 'Aprovado'
          : 'Rejeitado',
    value: opportunity.value
      ? `R$ ${opportunity.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : 'N/A',
    source: opportunity.source || 'N/A',
    products: opportunity.products_services?.join(', ') || 'N/A',
    unread_chats: opportunity.unread_chats || false,
  }));

  // Handler para trocar de aba
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1 && selectedOpportunity) {
      fetchChatMessages(selectedOpportunity.id);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Buscar mensagens do chat
  const fetchChatMessages = async (opportunityId: string) => {
    setLoadingChat(true);
    try {
      const res = await apiClient.get(`/opportunities/${opportunityId}/chat`);
      const messages = typeof res === 'object' && Array.isArray(res) ? res : [];
      // Sort messages by creation date in ascending order (oldest first)
      const sortedMessages = messages.sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      setChatMessages(sortedMessages);
      // Scroll to bottom after messages are loaded
      setTimeout(scrollToBottom, 100);
    } catch (e) {
      setChatMessages([]);
      enqueueSnackbar('Houve um erro ao carrgera mensagens.', { variant: 'error' });
    } finally {
      setLoadingChat(false);
    }
  };

  // Enviar nova mensagem
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedOpportunity || sendingMessage) return;

    setSendingMessage(true);
    try {
      const res = await apiClient.post(`/opportunities/${selectedOpportunity.id}/chat`, {
        message: newMessage.trim(),
        opportunity_id: selectedOpportunity.id,
        // user_id pode ser adicionado se necessário
      });
      setChatMessages((prev: any[]) => {
        const updatedMessages = [...prev, res];
        // Sort messages by creation date in ascending order (oldest first)
        return updatedMessages.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });
      setNewMessage('');
      // Scroll to bottom after sending message
      setTimeout(scrollToBottom, 100);
    } catch (e) {
      // erro ao enviar
      enqueueSnackbar('Houve um erro ao enviar mensagem', { variant: 'error' });
    } finally {
      setSendingMessage(false);
    }
  };

  // Enter para enviar
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Back button */}
      <BackButton />

      {/* Header section */}
      <Box
        sx={{
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '60%' }}>
            <Typography variant="h5">
              Oportunidades: gerencie e acompanhe o funil de vendas.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Aqui você encontra todas as suas oportunidades de negócio, podendo revisá-las,
              editá-las ou acompanhar sua evolução. Use os filtros para localizar rapidamente suas
              negociações e mantenha o controle total sobre seu pipeline de vendas.
            </Typography>
          </Box>
          <Box
            sx={{ width: '40%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <Button
              component={Link}
              to="/opportunities/new"
              variant="contained"
              sx={{
                bgcolor: 'var(--acer-dark)',
                '&:hover': { bgcolor: '#333' },
                textTransform: 'none',
                borderRadius: '1px',
                color: 'white',
                height: '40px',
                maxHeight: '40px',
              }}
            >
              Criar nova oportunidade
            </Button>
          </Box>
        </Box>
        {/* Tip box */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            bgcolor: '#fafff6',
            p: 2,
            borderRadius: 1,
          }}
        >
          <CheckCircleIcon
            sx={{
              color: '#2E7D32',
              fontSize: 16,
              border: '1px solid #2E7D32',
              borderRadius: '50%',
            }}
          />
          <Typography variant="body2" sx={{ color: '#2E7D32' }}>
            <span style={{ fontWeight: 500 }}>Dica:</span> Registre todas as oportunidades de
            negócio com potenciais clientes para acompanhar de perto a evolução de seu funil de
            vendas.
          </Typography>
        </Box>
      </Box>

      {/* Search and action section */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mb: 3,
        }}
      >
        <TextField
          placeholder="Pesquise por título, cliente ou status"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            flexGrow: 1,
            maxWidth: '500px',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* DataTable section */}
      <Paper sx={{ borderRadius: 1, overflow: 'hidden', p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <img
            src="/icons/opportunity.svg"
            alt="Opportunity icon"
            style={{
              width: 24,
              height: 24,
              marginRight: '8px',
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#2b3445', fontSize: '18px' }}>
            Minhas oportunidades
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Carregando oportunidades...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
            <Typography>Erro ao carregar oportunidades: {error}</Typography>
          </Box>
        ) : tableData.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Nenhuma oportunidade encontrada</Typography>
          </Box>
        ) : (
          <OpportunitiesTable data={tableData} onRowClick={handleTableRowClick} />
        )}
      </Paper>

      {/* Modal de detalhes da oportunidade */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        {selectedOpportunity && (
          <>
            <DialogTitle>{selectedOpportunity.title}</DialogTitle>
            <DialogContent dividers>
              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label="Detalhes" />
                <Tab label="Chat" />
              </Tabs>

              {activeTab === 0 && (
                <>
                  <Typography variant="body1" gutterBottom>
                    {selectedOpportunity.description || 'Sem descrição disponível'}
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Detalhes da oportunidade:
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Cliente:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedOpportunity.customer?.company_name ||
                            selectedOpportunity.client_company_name ||
                            'N/A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Valor:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedOpportunity.value
                            ? `R$ ${selectedOpportunity.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : 'N/A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Status:</Typography>
                        <Chip
                          label={
                            selectedOpportunity.status === 'Open'
                              ? 'Em negociação'
                              : selectedOpportunity.status === 'Won'
                                ? 'Aprovado'
                                : 'Rejeitado'
                          }
                          size="small"
                          color={
                            selectedOpportunity.status === 'Open'
                              ? 'primary'
                              : selectedOpportunity.status === 'Won'
                                ? 'success'
                                : 'error'
                          }
                        />
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Data de Fechamento:</Typography>
                        <Typography variant="body2">
                          {selectedOpportunity.estimated_closing_date ||
                          selectedOpportunity.expected_close_date
                            ? new Date(
                                selectedOpportunity.estimated_closing_date ||
                                  selectedOpportunity.expected_close_date ||
                                  ''
                              ).toLocaleDateString('pt-BR')
                            : 'N/A'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Produtos/Serviços:</Typography>
                        <Typography variant="body2">
                          {selectedOpportunity.products_services?.join(', ') || 'N/A'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </>
              )}

              {activeTab === 1 && (
                <Box sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                  <Box
                    ref={chatContainerRef}
                    sx={{
                      flexGrow: 1,
                      overflow: 'auto',
                      mb: 2,
                      pr: 1,
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a8a8a8',
                      },
                    }}
                  >
                    {loadingChat ? (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      chatMessages.map((message: any) => (
                        <Box
                          key={message.id}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mb: 2,
                            mr: 1,
                            alignItems: message.is_admin ? 'flex-start' : 'flex-end', // invertido
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: '70%',
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: message.is_admin ? 'grey.200' : 'primary.main', // invertido
                              color: message.is_admin ? 'text.primary' : 'white', // invertido
                              boxShadow: 1,
                            }}
                          >
                            <Typography variant="body2">{message.message}</Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, px: 0.5 }}
                          >
                            {message.is_admin ? 'Admin' : 'Usuário'} •{' '}
                            {new Date(message.created_at).toLocaleTimeString()}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Digite sua mensagem..."
                      variant="outlined"
                      size="small"
                      disabled={sendingMessage}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                      sx={{
                        minWidth: '40px',
                        minHeight: '40px',
                        '&.Mui-disabled': {
                          opacity: 0.6,
                        },
                      }}
                    >
                      {sendingMessage ? <CircularProgress size={20} /> : <Send />}
                    </IconButton>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default OpportunitiesPage;
